import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { statusLabel } from "../lib/labels";

const SPEC_ROWS = [
  ["Total land area", "area"],
  ["Plot sizes", "plot_sizes"],
  ["Villa configurations", "villa_configurations"],
  ["RERA registration", "rera"],
  ["Payment plan", "payment_plan"],
  ["Current availability", "availability"],
  ["Nearby landmarks", "landmarks"],
  ["Connectivity", "connectivity"],
];

const INFRA = ["Roads", "Electricity provision", "Water provision", "Drainage", "Green areas", "Security provision"];

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [config, setConfig] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="project-not-found">
        <h1 className="font-serif text-4xl text-[#FBF9F5]">Project not found</h1>
        <Link to="/projects" className="text-[#C5A059] text-sm mt-4 inline-block">Back to all projects</Link>
      </div>
    );
  }
  if (!project) {
    return <div className="pt-40 pb-32 text-center text-[#94A3B8]" data-testid="project-loading">Loading project…</div>;
  }

  const wa = config.whatsapp_realestate;

  return (
    <div data-testid={`project-detail-${project.slug}`}>
      <SEO title={`${project.location} — RK Builders`}
        description={`Residential project in ${project.location} by RK Builders. Status: ${statusLabel(project.status)}. ${project.overview || ""}`} />

      <header className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={project.image || "/images/realestate-land.jpg"} alt={project.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060A12] via-[#060A12]/50 to-[#060A12]/30" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <Eyebrow>
            <Link to="/projects" className="hover:text-[#FBF9F5] transition-colors">Projects</Link>
            <span className="mx-2 text-[#94A3B8]">/</span> {project.location}
          </Eyebrow>
          <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 max-w-3xl leading-[1.05]">
            {project.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#C5A059] text-[#060A12] px-3 py-1.5">
              {statusLabel(project.status)}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold border border-white/25 text-[#FBF9F5] px-3 py-1.5 capitalize">
              {project.type?.replace("_", " ")}
            </span>
            {project.is_placeholder && null}
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to={`/site-visit?project=${project.slug}`} data-testid="project-book-visit-btn"
              className="text-[11px] uppercase tracking-[0.22em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300">
              Book a Site Visit
            </Link>
            {wa && (
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Enquiry about project in ${project.location}`)}`}
                target="_blank" rel="noreferrer" data-testid="project-whatsapp-btn"
                className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-white/25 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300">
                WhatsApp Enquiry
              </a>
            )}
            {project.brochure_url && (
              <a href={project.brochure_url} target="_blank" rel="noreferrer" data-testid="project-brochure-btn"
                className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-8 py-4 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                Download Brochure
              </a>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Overview</Eyebrow>
            <p className="text-base text-[#94A3B8] leading-loose mt-5">{project.overview}</p>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] mt-14 mb-8">Specifications</h2>
            <dl className="border border-white/10">
              {SPEC_ROWS.map(([label, key], i) => (
                <div key={key} className={`grid grid-cols-2 gap-4 px-6 py-4 ${i % 2 ? "bg-[#0B1320]" : ""}`}>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-[#94A3B8]">{label}</dt>
                  <dd className="text-sm text-[#FBF9F5]/85 text-right">{project[key] || "To be confirmed"}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.12}>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] mt-14 mb-8">Development Provisions</h2>
            {project.facilities?.length ? (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {project.facilities.map((f) => (
                  <li key={f} className="text-sm text-[#FBF9F5]/80 border border-white/10 px-5 py-4">{f}</li>
                ))}
              </ul>
            ) : (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
                  {INFRA.map((f) => (
                    <div key={f} className="bg-[#060A12] p-5 text-xs uppercase tracking-[0.15em] text-[#94A3B8]">{f}</div>
                  ))}
                </div>
                <p className="text-xs text-[#C5A059]/80 mt-4">
                  Only facilities confirmed for this specific project will be displayed. Confirmations pending.
                </p>
              </div>
            )}
          </Reveal>

          {project.drone_video && (
            <Reveal delay={0.15}>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF9F5] mt-14 mb-8">Drone View</h2>
              <video src={project.drone_video} controls className="w-full border border-white/10" data-testid="project-drone-video" />
            </Reveal>
          )}
        </div>

        <aside className="lg:col-span-5">
          <Reveal delay={0.1} className="border border-[#C5A059]/25 bg-[#0B1320] p-8 sticky top-28">
            <Eyebrow>Price &amp; Availability</Eyebrow>
            <p className="font-serif text-3xl text-[#FBF9F5] mt-4">{project.price}</p>
            <p className="text-xs text-[#94A3B8] mt-3 leading-relaxed">{project.availability}</p>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mb-3">Location</p>
              {project.maps_url && (
                <a href={project.maps_url} target="_blank" rel="noreferrer" data-testid="project-maps-btn"
                  className="text-[11px] uppercase tracking-[0.18em] font-semibold border border-[#C5A059]/50 text-[#C5A059] px-4 py-2.5 inline-block hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                  Open in Google Maps
                </a>
              )}
            </div>
            <p className="text-[11px] text-[#94A3B8]/80 leading-relaxed mt-8 pt-6 border-t border-white/10" data-testid="project-disclaimer">
              {project.disclaimer}
            </p>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
